import { useMemo } from 'react';
import type { Intervention, Mechanic } from '@/lib/types';
import { addDays, formatHeureMur, fromIsoDate, toIsoDate } from '@/lib/dates';

const DAY_LABELS = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'];

type Props = {
  weekStart: string;
  mechanics: Mechanic[];
  interventions: Intervention[];
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onTodayWeek: () => void;
  onClickIntervention: (i: Intervention) => void;
  onDayHeaderClick?: (iso: string) => void;
};

export default function PlanningWeekView({
  weekStart,
  mechanics,
  interventions,
  onPrevWeek,
  onNextWeek,
  onTodayWeek,
  onClickIntervention,
  onDayHeaderClick,
}: Props) {
  const days = useMemo(() => {
    const start = fromIsoDate(weekStart);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [weekStart]);

  const today = toIsoDate(new Date());
  const startMonth = days[0]!.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const endMonth = days[6]!.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const headerLabel = startMonth === endMonth ? startMonth : `${startMonth} – ${endMonth}`;

  // Index : mecanoId -> dateIso -> Intervention[]
  const byMecanoDay = useMemo(() => {
    const map = new Map<string, Map<string, Intervention[]>>();
    for (const i of interventions) {
      const mecKey = i.mecanicienId;
      const iso = i.dateRdv.slice(0, 10);
      const days = map.get(mecKey) ?? new Map<string, Intervention[]>();
      const list = days.get(iso) ?? [];
      list.push(i);
      days.set(iso, list);
      map.set(mecKey, days);
    }
    for (const [, dayMap] of map) {
      for (const [, list] of dayMap) {
        list.sort((a, b) => a.creneauDebut.localeCompare(b.creneauDebut));
      }
    }
    return map;
  }, [interventions]);

  // Total RDV par mécano (sur la semaine, hors annulés)
  const totalsByMecano = useMemo(() => {
    const map = new Map<string, { count: number; minutes: number }>();
    for (const i of interventions) {
      if (i.statut === 'ANNULE') continue;
      const prev = map.get(i.mecanicienId) ?? { count: 0, minutes: 0 };
      map.set(i.mecanicienId, {
        count: prev.count + 1,
        minutes: prev.minutes + i.dureeEstimeeMinutes,
      });
    }
    return map;
  }, [interventions]);

  // grid-cols-[140px_repeat(7,minmax(0,1fr))] — colonne fixe mécano + 7 jours égaux
  const gridTemplate = '140px repeat(7, minmax(0, 1fr))';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-text-primary capitalize">{headerLabel}</h2>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onPrevWeek}
            className="h-8 w-8 flex items-center justify-center border border-border-light rounded-md text-text-secondary hover:bg-surface-hover"
            aria-label="Semaine précédente"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={onTodayWeek}
            className="h-8 px-3 text-xs font-medium border border-border-light rounded-md text-text-secondary hover:bg-surface-hover"
          >
            Aujourd'hui
          </button>
          <button
            type="button"
            onClick={onNextWeek}
            className="h-8 w-8 flex items-center justify-center border border-border-light rounded-md text-text-secondary hover:bg-surface-hover"
            aria-label="Semaine suivante"
          >
            ›
          </button>
        </div>
      </div>

      <div className="border border-border-light rounded-md overflow-hidden bg-border-light">
        {/* Header row : Mécano | LUN | MAR | … | DIM */}
        <div className="grid bg-surface-white" style={{ gridTemplateColumns: gridTemplate }}>
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-text-muted border-r border-border-light">
            Mécanicien
          </div>
          {days.map((day, idx) => {
            const iso = toIsoDate(day);
            const isToday = iso === today;
            const isPast = iso < today;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => onDayHeaderClick?.(iso)}
                title="Ouvrir la vue Jour"
                className={`px-2 py-2 text-center border-r last:border-r-0 border-border-light transition-colors hover:bg-ktm-orange-light ${
                  isToday ? 'bg-ktm-orange-light' : ''
                }`}
              >
                <div
                  className={`text-[10px] font-semibold uppercase tracking-wider ${
                    isToday ? 'text-ktm-orange' : 'text-text-muted'
                  }`}
                >
                  {DAY_LABELS[idx]}
                </div>
                <div
                  className={`text-sm font-semibold ${
                    isToday
                      ? 'text-ktm-orange'
                      : isPast
                        ? 'text-text-disabled'
                        : 'text-text-primary'
                  }`}
                >
                  {day.getDate()}
                </div>
              </button>
            );
          })}
        </div>

        {/* Une row par mécano */}
        {mechanics.length === 0 ? (
          <div className="bg-surface-white px-4 py-8 text-center text-text-muted text-sm">
            Aucun mécanicien actif pour ce type d'agenda.
          </div>
        ) : (
          mechanics.map((mec) => {
            const totals = totalsByMecano.get(mec.id);
            const dayMap = byMecanoDay.get(mec.id);
            return (
              <div
                key={mec.id}
                className="grid bg-surface-white border-t border-border-light"
                style={{ gridTemplateColumns: gridTemplate }}
              >
                {/* Header mécano */}
                <div className="px-3 py-2 border-r border-border-light flex flex-col justify-center">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: mec.couleur }}
                      aria-hidden
                    />
                    <span className="text-sm font-medium text-text-primary truncate">
                      {mec.prenom}
                    </span>
                  </div>
                  <div className="text-[10px] text-text-muted font-mono mt-0.5">
                    {totals ? `${totals.count} RDV · ${formatHours(totals.minutes)}` : '—'}
                  </div>
                </div>

                {/* 7 colonnes jours */}
                {days.map((day) => {
                  const iso = toIsoDate(day);
                  const isPast = iso < today;
                  const isToday = iso === today;
                  const items = dayMap?.get(iso) ?? [];

                  return (
                    <div
                      key={iso}
                      className={`p-1 flex flex-col gap-1 min-h-[80px] border-r last:border-r-0 border-border-light ${
                        isToday ? 'bg-ktm-orange-light/30' : isPast ? 'bg-surface-bg' : ''
                      }`}
                    >
                      {items.length === 0 ? (
                        <div className="flex-1" />
                      ) : (
                        items.map((i) => (
                          <WeekCard
                            key={i.id}
                            intervention={i}
                            onClick={() => onClickIntervention(i)}
                          />
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function WeekCard({ intervention, onClick }: { intervention: Intervention; onClick: () => void }) {
  const isCancelled = intervention.statut === 'ANNULE';
  const statusDot = STATUS_DOT[intervention.statut] ?? 'bg-border-medium';

  return (
    <button
      type="button"
      onClick={onClick}
      style={{ borderLeftColor: intervention.mecanicien.couleur }}
      className={`text-left bg-surface-white border border-border-light border-l-4 rounded-sm px-1.5 py-1 hover:shadow-sm hover:border-ktm-orange transition-all ${
        isCancelled ? 'opacity-50 line-through' : ''
      } ${intervention.urgent ? 'border-status-annule-border ring-1 ring-status-annule-border/30' : ''}`}
    >
      <div className="flex items-center gap-1 text-[10px] font-mono text-text-muted">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot}`} aria-hidden />
        <span>{formatHeureMur(intervention.creneauDebut)}</span>
        {intervention.urgent && (
          <span className="text-status-annule-text font-bold ml-auto">!</span>
        )}
      </div>
      <div className="text-[11px] font-medium text-text-primary truncate leading-tight">
        {intervention.nature}
      </div>
      <div className="text-[10px] text-text-muted truncate leading-tight font-mono">
        {intervention.moto.vin}
      </div>
    </button>
  );
}

function formatHours(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

const STATUS_DOT: Record<string, string> = {
  CONFIRME: 'bg-status-confirme-text',
  EN_ATTENTE: 'bg-status-en-attente-text',
  EN_COURS: 'bg-status-en-cours-text',
  ATTENTE_PIECES: 'bg-status-attente-pieces-text',
  TERMINE: 'bg-status-termine-text',
  LIVRE: 'bg-status-livre-text',
  ANNULE: 'bg-status-annule-text',
};
